import type { TripPosition } from '../api'

const METERS_PER_LATITUDE_DEGREE = 111_320

export function routeSpanMeters(positions: TripPosition[]): number {
  if (positions.length < 2) return 0

  let minimumLatitude = positions[0]!.latitude
  let maximumLatitude = minimumLatitude
  let minimumLongitude = positions[0]!.longitude
  let maximumLongitude = minimumLongitude

  for (const position of positions.slice(1)) {
    minimumLatitude = Math.min(minimumLatitude, position.latitude)
    maximumLatitude = Math.max(maximumLatitude, position.latitude)
    minimumLongitude = Math.min(minimumLongitude, position.longitude)
    maximumLongitude = Math.max(maximumLongitude, position.longitude)
  }

  const middleLatitudeRadians = ((minimumLatitude + maximumLatitude) / 2) * Math.PI / 180
  const latitudeMeters = (maximumLatitude - minimumLatitude) * METERS_PER_LATITUDE_DEGREE
  const longitudeMeters = (maximumLongitude - minimumLongitude)
    * METERS_PER_LATITUDE_DEGREE
    * Math.cos(middleLatitudeRadians)

  return Math.hypot(latitudeMeters, longitudeMeters)
}

/**
 * Normal routes should use Google Maps' exact fitBounds zoom. Tiny routes get
 * enough surrounding context to remain recognisable without looking like a
 * small dot in the middle of the map.
 */
export function maximumInitialRouteZoom(positions: TripPosition[]): number | undefined {
  const span = routeSpanMeters(positions)
  if (span <= 250) return 17
  if (span <= 600) return 16
  return undefined
}

export function focusedRouteZoom(currentZoom: number | undefined): number {
  return Math.min(17, Math.max(16, (currentZoom ?? 14) + 2))
}

function worldPoint(position: TripPosition): { x: number; y: number } {
  const latitude = Math.max(-85.05112878, Math.min(85.05112878, position.latitude))
  const sine = Math.sin(latitude * Math.PI / 180)
  return {
    x: (position.longitude + 180) / 360,
    y: .5 - Math.log((1 + sine) / (1 - sine)) / (4 * Math.PI),
  }
}

export function routeFitsAtZoom(
  positions: TripPosition[],
  zoom: number,
  viewportWidth: number,
  viewportHeight: number,
  padding = 36,
): boolean {
  if (positions.length < 2) return true

  const points = positions.map(worldPoint)
  const xs = points.map((point) => point.x)
  const ys = points.map((point) => point.y)
  const worldSize = 256 * 2 ** zoom
  const routeWidth = (Math.max(...xs) - Math.min(...xs)) * worldSize
  const routeHeight = (Math.max(...ys) - Math.min(...ys)) * worldSize

  return routeWidth <= viewportWidth - padding * 2
    && routeHeight <= viewportHeight - padding * 2
}
